(() => {
  if (window.wimRv2Loaded) return;
  window.wimRv2Loaded = true;

  const prefix = 'wim_rv2_handles_v1:';
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value);
  const validId = value => typeof value === 'number' ? Number.isSafeInteger(value) && value > 0 : typeof value === 'string' && /^[1-9][0-9]{0,19}$/.test(value);
  const normalize = values => Array.isArray(values) ? [...new Set(values.filter(validHandle))].slice(0, 12) : [];
  let generation = 0;
  let suppressed = false;
  let loadingPrivacy = false;
  const requests = new Set();

  const config = () => {
    const element = document.querySelector('[data-wim-rv2-config]');
    if (!element) return null;
    const data = element.dataset;
    if (data.enabled !== 'true' || !/^[A-Z]{2}$/.test(data.country || '') || data.country === 'GY' || data.country !== data.expectedCountry || data.shop !== data.expectedShop) return null;
    if (!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(data.shop || '')) return null;
    if (!/^[A-Z]{3}$/.test(data.currency || '')) return null;
    if (window.Shopify?.currency?.active && window.Shopify.currency.active !== data.currency) return null;
    try {
      if (typeof data.root !== 'string' || !/^\/(?!\/)/.test(data.root)) return null;
      const root = new URL(data.root, location.origin);
      if (root.origin !== location.origin || root.search || root.hash || root.pathname.includes('\\')) return null;
      root.pathname = `${root.pathname.replace(/\/$/, '')}/`;
      return { element, key: `${prefix}${data.shop}:${data.country}`, root, currency: data.currency, handle: data.handle, country: data.country, shop: data.shop };
    } catch { return null; }
  };

  const allowed = () => {
    try { return window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.() === true; }
    catch { return false; }
  };
  const read = key => {
    if (!allowed()) return [];
    try {
      const raw = localStorage.getItem(key) || '[]';
      return raw.length <= 32768 ? normalize(JSON.parse(raw)) : [];
    } catch { return []; }
  };
  const save = (key, handles) => {
    if (!allowed() || suppressed) return;
    try { localStorage.setItem(key, JSON.stringify(normalize(handles))); } catch {}
  };
  const remove = key => {
    try { localStorage.removeItem(key); return true; } catch { return false; }
  };
  const cancel = () => {
    ++generation;
    requests.forEach(controller => controller.abort());
    requests.clear();
  };
  const rows = () => [...document.querySelectorAll('[data-wim-rv2-row]')];
  const hide = () => {
    rows().forEach(row => {
      row.hidden = true;
      row.querySelector('[data-wim-rv2-list]')?.replaceChildren();
    });
  };
  const clearStatus = () => {
    document.querySelectorAll('[data-wim-rv2-status]').forEach(status => { status.textContent = ''; });
  };
  const active = (run, cfg) => {
    const current = config();
    return run === generation && !suppressed && allowed() && current?.element === cfg.element && current.key === cfg.key && current.root.href === cfg.root.href && current.currency === cfg.currency;
  };
  const imageSource = value => {
    try {
      if (typeof value !== 'string' || !value) return null;
      const source = new URL(value, location.origin);
      if (source.protocol !== 'https:' || source.username || source.password) return null;
      if (source.origin !== location.origin && source.hostname !== 'cdn.shopify.com') return null;
      source.searchParams.set('width', '420');
      return source.href;
    } catch { return null; }
  };
  const fetchProduct = async (handle, cfg, run) => {
    if (!active(run, cfg)) return null;
    const controller = new AbortController();
    requests.add(controller);
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(new URL(`products/${encodeURIComponent(handle)}.js`, cfg.root).href, {
        credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: controller.signal
      });
      if (!response.ok || !active(run, cfg)) return null;
      const product = await response.json();
      if (product.handle !== handle || product.available !== true || typeof product.title !== 'string' || !product.title.trim() || !Array.isArray(product.variants)) return null;
      const variants = product.variants.filter(variant => variant.available === true && validId(variant.id) && Number.isSafeInteger(variant.price) && variant.price >= 0);
      if (!variants.length) return null;
      const minimum = Math.min(...variants.map(variant => variant.price));
      const variant = variants.find(item => item.price === minimum);
      const media = imageSource(variant.featured_image?.src) || imageSource(product.featured_image);
      if (!media) return null;
      return { handle, id: String(product.id), title: product.title, media, variantId: variant.id, minimum, varies: variants.some(item => item.price !== minimum) };
    } catch { return null; }
    finally { clearTimeout(timeout); requests.delete(controller); }
  };

  const membership = async (row, cfg, run, handles) => {
    if (row.dataset.collectionHandle !== 'asian-grocery' || !validId(row.dataset.collectionId)) return null;
    const members = new Map();
    let totalPages = 1;
    for (let page = 1; page <= totalPages; page++) {
      if (!active(run, cfg)) return null;
      const controller = new AbortController();
      requests.add(controller);
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const url = new URL('collections/asian-grocery', cfg.root);
        url.searchParams.set('section_id', 'wim-rv2-membership');
        url.searchParams.set('page', String(page));
        const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: controller.signal });
        if (!response.ok || !active(run, cfg)) return null;
        const html = await response.text();
        if (html.length > 300000) return null;
        const marker = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-wim-rv2-membership]');
        if (!marker) return null;
        const data = JSON.parse(marker.textContent);
        if (data.shop !== cfg.shop || data.country !== cfg.country || data.collectionHandle !== 'asian-grocery' || data.collectionId !== row.dataset.collectionId || data.page !== page || !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 100 || !Array.isArray(data.products) || data.products.length > 250) return null;
        if (page !== 1 && data.pages !== totalPages) return null;
        totalPages = data.pages;
        for (const member of data.products) {
          if (!validHandle(member.handle) || !validId(member.id)) return null;
          members.set(member.handle, String(member.id));
        }
        if (handles.every(handle => members.has(handle))) break;
      } catch { return null; }
      finally { clearTimeout(timeout); requests.delete(controller); }
    }
    return active(run, cfg) ? members : null;
  };

  const card = (item, row, cfg, formatter) => {
    const entry = document.createElement('li');
    const link = document.createElement('a');
    const target = new URL(`products/${encodeURIComponent(item.handle)}`, cfg.root);
    target.searchParams.set('variant', item.variantId);
    link.href = target.href;
    link.className = 'wim-rv2__card';
    const imageWrap = document.createElement('div');
    imageWrap.className = 'wim-rv2__image';
    const image = document.createElement('img');
    image.src = item.media;
    image.alt = '';
    image.width = 420;
    image.height = 420;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.addEventListener('error', () => {
      entry.remove();
      row.hidden = !row.querySelector('[data-wim-rv2-list]')?.children.length;
    }, { once: true });
    imageWrap.append(image);
    const title = document.createElement('h3');
    title.textContent = item.title;
    const price = document.createElement('strong');
    price.textContent = `${item.varies ? row.dataset.from + ' ' : ''}${formatter.format(item.minimum / 100)}`;
    const action = document.createElement('span');
    action.textContent = row.dataset.view;
    link.append(imageWrap, title, price, action);
    entry.append(link);
    return entry;
  };
  const bindClear = (row, cfg) => {
    const button = row.querySelector('[data-wim-rv2-clear]');
    if (!button) return;
    button.onclick = () => {
      suppressed = true;
      cancel();
      const removed = remove(cfg.key);
      hide();
      clearStatus();
      const status = row.closest('[data-wim-rv2-mount]')?.querySelector('[data-wim-rv2-status]');
      if (status) {
        status.textContent = removed ? row.dataset.cleared : row.dataset.clearBlocked;
        status.focus({ preventScroll: true });
      }
    };
  };
  const refresh = async (record = false) => {
    cancel();
    const run = generation;
    hide();
    const cfg = config();
    if (!cfg) return;
    if (!allowed()) { remove(cfg.key); clearStatus(); return; }
    if (suppressed) return;
    if (record && validHandle(cfg.handle)) save(cfg.key, [cfg.handle, ...read(cfg.key).filter(handle => handle !== cfg.handle)]);
    const mounts = rows();
    if (!mounts.length) return;
    mounts.forEach(row => bindClear(row, cfg));
    const handles = read(cfg.key);
    if (!handles.length) return;
    const allowedByRow = new Map();
    for (const row of mounts) {
      if (row.dataset.scope === 'asian') allowedByRow.set(row, await membership(row, cfg, run, handles));
      else if (!row.dataset.scope || row.dataset.scope === 'all') allowedByRow.set(row, true);
      else allowedByRow.set(row, null);
    }
    if (!active(run, cfg)) return;
    const candidates = handles.filter(handle => mounts.some(row => {
      const allowlist = allowedByRow.get(row);
      return allowlist === true || allowlist?.has(handle);
    }));
    if (!candidates.length) return;
    let formatter;
    try {
      formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cfg.currency, currencyDisplay: 'code' });
    } catch { return; }
    const products = [];
    for (let offset = 0; offset < candidates.length; offset += 3) {
      if (!active(run, cfg)) return;
      const batch = await Promise.all(candidates.slice(offset, offset + 3).map(handle => fetchProduct(handle, cfg, run)));
      products.push(...batch.filter(Boolean));
    }
    if (!active(run, cfg)) return;
    mounts.forEach(row => {
      const list = row.querySelector('[data-wim-rv2-list]');
      if (!list) return;
      const limit = Math.min(12, Math.max(2, Number(row.dataset.limit) || 8));
      const allowlist = allowedByRow.get(row);
      products.filter(item => allowlist === true || allowlist?.get(item.handle) === item.id).slice(0, limit).forEach(item => list.append(card(item, row, cfg, formatter)));
      row.hidden = !list.children.length;
    });
  };
  const initialize = (record = true) => {
    if (!config()) { cancel(); hide(); return; }
    if (window.Shopify?.customerPrivacy) { void refresh(record); return; }
    hide();
    if (loadingPrivacy || !window.Shopify?.loadFeatures) return;
    loadingPrivacy = true;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], error => {
        loadingPrivacy = false;
        if (!error) void refresh(record);
      });
    } catch { loadingPrivacy = false; }
  };

  document.addEventListener('visitorConsentCollected', () => { void refresh(true); });
  document.addEventListener('shopify:section:load', () => initialize(false));
  document.addEventListener('shopify:section:unload', () => { cancel(); hide(); });
  window.addEventListener('pageshow', event => { if (event.persisted) initialize(true); });
  window.addEventListener('pagehide', () => { cancel(); hide(); });
  window.addEventListener('storage', event => {
    const cfg = config();
    if (cfg && (event.key === cfg.key || event.key === null)) void refresh(false);
  });
  initialize();
})();
